import React, { useState } from 'react';
import { Box, Typography, Paper, Button, CircularProgress, Alert } from '@mui/material';
import { Mail, RefreshCw } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';

export const EmailVerificationRequired: React.FC = () => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResendVerification = async () => {
    setResending(true);
    setError(null);

    try {
      // Get user's access token
      const token = await getAccessTokenSilently();

      // Call our backend endpoint which will call Auth0 Management API
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://backend.veltus.io'}/auth/resend-email-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: user?.sub,
        }),
      });

      if (response.ok) {
        setResent(true);
        setTimeout(() => setResent(false), 5000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to resend verification email');
      }
    } catch (err) {
      console.error('Error resending verification:', err);
      setError(err instanceof Error ? err.message : 'Failed to resend verification email. Please try again or contact support.');
    } finally {
      setResending(false);
    }
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 3,
      }}
    >
      <Paper
        sx={{
          maxWidth: 500,
          width: '100%',
          p: 4,
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: '#E3F2FD',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <Mail size={40} color="#1976d2" />
        </Box>

        <Typography variant="h5" fontWeight={600} gutterBottom>
          Verify Your Email
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          We've sent a verification email to <strong>{user?.email}</strong>
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please check your inbox and click the verification link to activate your account.
          Don't forget to check your spam folder!
        </Typography>

        {resent && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Verification email sent! Check your inbox.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={resending ? <CircularProgress size={18} /> : <RefreshCw size={18} />}
            onClick={handleResendVerification}
            disabled={resending}
            fullWidth
          >
            {resending ? 'Sending...' : 'Resend Verification Email'}
          </Button>

          <Button
            variant="text"
            onClick={handleLogout}
            fullWidth
          >
            Logout
          </Button>
        </Box>

        <Button
          variant="contained"
          startIcon={<RefreshCw size={18} />}
          onClick={() => window.location.reload()}
          fullWidth
          sx={{ mt: 2 }}
        >
          I've Verified My Email - Refresh
        </Button>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Click the button above after verifying your email via the link in your inbox.
        </Typography>
      </Paper>
    </Box>
  );
};
